import { types as t, SnapshotOrInstance, getEnv, tryResolve, SnapshotIn, IAnyStateTreeNode, Instance } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import DataSource from './datasource'
import Component from './component'
import NavigationItem from './navigation'
import AssignmentItem from './assignment'
import AssignmentVersion from './assignment-version'
import Theme from './theme'
import { MappingItem } from './mapping'
import { nanoid as shortid } from 'nanoid'
import { withoutPatch } from '../../utils/without-patch'
import chunk from 'lodash/chunk'
import { transaction } from 'mobx'
import generalProps from '../../utils/general-props'
import WorkflowConfig from './workflow-config'

export type AppItem = (
  typeof Component | 
  typeof NavigationItem | 
  typeof AssignmentItem | 
  typeof AssignmentVersion | 
  typeof DataSource | 
  typeof Theme | 
  typeof MappingItem
)

export const AppInfo = t
  .model('AppInfo', {
    id: t.identifier,
    name: t.maybeNull(t.string),
    description: t.maybeNull(t.string),
    imageUrl: t.maybeNull(t.string),
    markup: t.maybeNull(t.string)
  })
  .actions(crudActions)

const MappingMap = t.map(MappingItem)

export const AppDetails = t.model('AppDetails', {
  key: t.identifier,
  layouts: t.map(Component),
  pages: t.map(Component),
  navigation: t.map(NavigationItem),
  assignments: t.map(AssignmentItem),
  assignmentVersions: t.map(AssignmentVersion),
  dataSources: t.map(DataSource),
  components: t.map(Component),
  themes: t.map(Theme),
  workflowConfigs: t.map(WorkflowConfig),
  mapping: MappingMap
})
.volatile(() => ({
  /*
  mobile, tablet or nothing
  */
  deviceContext: 'desktop',
  groupContext: '',
  loadingProgress: 0,
  loadingIntent: 'success',
  loadNow: false
}))
.actions(self => ({
  newMapping (m: SnapshotOrInstance<typeof MappingItem> = {}) {
    const mapping = { ...m, id: (m && m.id) || shortid() }
    return self.mapping.put(mapping)
  },
  
}))
.views(self => ({
  getOrLoadObject (name: string, key: string) {
    const resolved = self[name] && self[name].get(key)
    if (resolved) return resolved

    if (name === 'mapping') {
      const env = getEnv(self)
      const m = env?.cache?.mapping?.[key]

      if (m) {
        withoutPatch(self, () =>
        self.newMapping(m))
      }
      return self.mapping.get(key)
    }
  },
  getItemAtUrl (pathname: string, url: string[]) {
    return resolve(self, [undefined, pathname, ...url].join('/')) || {}
  },
  // get all assignment.group in current deviceContext (used in group switcher)
  get assignmentsGroups () {
    return Array.from(new Set(Array.from(self.assignments.values()).filter(a => a.device === self.deviceContext).map(a => a.group)))
  },
}))
.views(self => ({
  tryGetDefaultGroup () {
    return self.assignmentsGroups.find(g => g === self.key)
  }
}))
.views(self => ({
  get assignment () {
    if(self.assignments.size === 1) return Array.from(self.assignments.values())[0]
    // if groupContext not set yet (or has invalid value), take default group (appId) or first one
    const groupCtx = self.assignmentsGroups.find(g => g === self.groupContext) ? self.groupContext : (self.tryGetDefaultGroup() || self.assignmentsGroups[0])
    return Array.from(self.assignments.values()).find(a => a.device === self.deviceContext && a.group === groupCtx)
  }
}))
.views(self => ({
    get versions () {
      return (self.assignment && Array.from(self.assignmentVersions.values()).filter(v => v.assignment === self.assignment)) || []
    },
    get layout () {
      return self.assignment?.layout
    },
    get nav () {
      return self.assignment?.navigation
    },
    get theme () {
      return self.assignment?.theme
    },
    get refsNeeded () {
      const env = getEnv(self)
      if (env?.refsNeeded) return env.refsNeeded
      return Array.from(self.dataSources.values()).reduce((refs, ds) => {
        if (ds.fields) {
          for (const { referenceData } of ds.fields) {
            const ard = Array.prototype.concat(referenceData).filter(x => x)
            for (const rd of ard) {
              const dsid = rd.dataSourceId
              refs[dsid] = refs[dsid] || []
              if(!refs[dsid].includes(rd.value)) refs[dsid].push(rd.value)
            }
          }
        }
        return refs
      }, {})
    },
    tree (type: 'data' | 'function') {
      return Array.from(self.dataSources.values())
        .map(ds => {
          const dsNode = typeof ds.labels === 'function' ? ds.labels() : ds.labels
          let meta = ds
          if (dsNode) {
              if (dsNode.meta) meta = dsNode.meta
              let childNodes = []
              let asynch: boolean
              let fetchChildNodes

              if (dsNode.childNodes) {
		let node, stateNode
		if (type === 'data') {
                  node = dsNode.childNodes.find(c => c.key === 'data')
                  stateNode = dsNode.childNodes.find(c => c.key === 'state')
		}
		if (type === 'function') {
                  node = dsNode.childNodes.find(c => c.key === 'methods')
		}
		if (node) {
                  if (node.childNodes) childNodes = node.childNodes
                  asynch = node.asynch
                  fetchChildNodes = ds.fetchChildNodes
		}
		if (type === 'data' && stateNode) {
                  childNodes.unshift({ ...stateNode, key: '$' })
		}
              }
              return {
		...dsNode,
		meta,
		asynch,
		fetchChildNodes,
		childNodes,
		type: 'dataSource'
              }
            }
            return undefined
          })
          .filter(d => d)
    }
}))
  .views(self => ({
    get dataTree () {
      return self.tree('data')
    },
    get functionTree () {
      return self.tree('function')
    }
  }))

  
  .actions(crudActions)
  .actions(self => ({
    idleLoad (chunks: SnapshotIn<typeof MappingMap>[], current: number) {
      if (!chunks?.[current]?.length) return
      let next = chunks[current]
      if (self.loadNow) {
        next = chunks.slice(current).flat() as any
        current = chunks.length - 1
      }
      const fn = () => {
        withoutPatch(self, () =>
          transaction(() =>
            this.newMappings((next as any).filter(m => !self.mapping.has(m.id)))))

        this.setLoadingProgress((current + 1) / chunks.length)
        this.idleLoad(chunks, current + 1)
      }

      if (!window.requestIdleCallback) {
        // safarIE
        fn()
      } else {
        window.requestIdleCallback(fn)
      }
    },
    setLoadNow (s) {
      self.loadNow = s
    },
    setDeviceContext (ctx: string) {
      self.deviceContext = ctx
    },
    setGroupContext (ctx: string) {
      self.groupContext = ctx
    },
    setLoadingProgress (num: number, intent = 'primary') {
      self.loadingProgress = num
      self.loadingIntent = intent
    },
    newComponent (path: string, comp: SnapshotOrInstance<typeof Component> = { name: 'New' }, cmpType: string) {
      const newComp = {
        ...comp,
        type: cmpType || comp.type,
        id: comp.id || shortid()
      } as SnapshotOrInstance<typeof Component>

      if (path === 'pages' || path === 'layouts' || path === 'components') {
        if (!cmpType) newComp.type = path && path[0].toUpperCase() + path.slice(1, -1)
        newComp.components = newComp.components || []
        if (!newComp.mapping) {
          newComp.mapping = {
            id: shortid(),
            children: [...generalProps],
            type: 'object'
          }
        }
        const newMappings = comp => {
          if (comp.components) comp.components.map(newMappings)
          if (comp.mapping && comp.mapping.id) {
            comp.mapping = self.newMapping(comp.mapping).id
          }
        }
        newMappings(newComp)
      }
      self[path] && self[path].put(newComp)
      return self[path].get(newComp.id)
    },
    newMappings (a = []) {
      for (const m of a) {
        try {
          self.mapping.set(m.id, m)
        } catch (e) {
          console.error('failed loading ', m.id, e)
        }
      }
    },
    newDataSource (ds: SnapshotOrInstance<typeof DataSource>) {
      self.dataSources.put(ds)
    }
  }))
  .actions(self => ({
    loadMapping (mapping: SnapshotIn<typeof MappingMap>) {
      if (!mapping) return
      const CHUNK_SIZE = 5
      const arr = Object.values(mapping)
      const chunks: SnapshotIn<typeof MappingItem>[] = chunk(arr, CHUNK_SIZE)
      return self.idleLoad(chunks, 0)
    }}))
  .actions(self => ({
    afterCreate () {
      const env = getEnv(self)
      self.loadMapping(env?.cache?.mapping)
    },
    createEnvDSIfNeeded () {
      if(!self.dataSources.get('env')) {
        self.newDataSource({
          id: 'env',
          name: 'Environment Variables',
          type: 'StackContext'
        })
      }
    }
  }))

export const resolve = (node: IAnyStateTreeNode, path: string) => {
    try {
      return tryResolve(node, path)
    } catch (e) {
      
    }
  }
